import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, IconButton, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import RadarRoundedIcon from '@mui/icons-material/RadarRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { login, verifyMfa } from '../../services/api/authService';
import { tokens } from '../../theme/theme';
import BrandLogo from '../../components/branding/BrandLogo';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = challenge
        ? await verifyMfa(challenge, code)
        : await login(email.trim(), password);
      if (result.mfaRequired) setChallenge(result.mfaChallengeToken);
      else navigate(location.state?.from || '/', { replace: true });
    } catch (err) {
      const d = err?.response?.data;
      setError({
        code: d?.code || 'CG-AUTH-LOGIN',
        message: d?.message || 'Login failed.',
        guidance: 'Verify your credentials and try again.'
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: { xs: 1.5, md: 3 }, background: tokens.brand.gradient, position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', width: 480, height: 480, borderRadius: '50%', border: '1px solid rgba(34,199,232,.16)', top: -210, right: -120 }, '&::after': { content: '""', position: 'absolute', width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(22,184,212,.16), transparent 68%)', bottom: -160, left: -80 } }}>
      <Paper elevation={24} sx={{ width: '100%', maxWidth: 1040, minHeight: { md: 650 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.08fr .92fr' }, overflow: 'hidden', borderRadius: { xs: 2.5, md: 4 }, position: 'relative', zIndex: 1, border: '1px solid rgba(255,255,255,.12)' }}>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', justifyContent: 'space-between', p: 6, background: 'linear-gradient(145deg, rgba(7,24,45,.98), rgba(8,52,72,.94)), radial-gradient(circle at 85% 15%, rgba(34,199,232,.30), transparent 42%)', position: 'relative' }}>
          <BrandLogo dark />
          <Box>
            <Chip icon={<ShieldRoundedIcon />} label="Enterprise cyber defence" sx={{ mb: 3, color: tokens.brand.cyan, bgcolor: 'rgba(22,184,212,.10)', border: '1px solid rgba(34,199,232,.28)', '& .MuiChip-icon': { color: tokens.brand.cyan } }} />
            <Typography variant="h2" sx={{ fontSize: '3.25rem', lineHeight: 1.04, maxWidth: 480, mb: 2 }}>Protect what<br /><Box component="span" sx={{ color: tokens.brand.cyan }}>matters most.</Box></Typography>
            <Typography sx={{ color: tokens.text.secondary, fontSize: '1rem', lineHeight: 1.7, maxWidth: 440 }}>Unified threat detection, continuous monitoring and coordinated response for your hybrid infrastructure.</Typography>
          </Box>
          <Stack direction="row" spacing={3} sx={{ color: tokens.text.secondary }}>
            {[[RadarRoundedIcon, 'Monitor'], [VerifiedUserRoundedIcon, 'Protect'], [LockRoundedIcon, 'Respond']].map(([Icon, label]) => <Stack key={label} direction="row" spacing={1} alignItems="center"><Icon sx={{ color: tokens.brand.main, fontSize: 20 }} /><Typography variant="caption">{label}</Typography></Stack>)}
          </Stack>
        </Box>
        <Stack spacing={2.4} component="form" onSubmit={submit} sx={{ justifyContent: 'center', px: { xs: 2.5, sm: 6 }, py: { xs: 4, sm: 6 }, bgcolor: tokens.bg.surface }}>
          <BrandLogo sx={{ display: { xs: 'block', md: 'none' }, mb: 1 }} />
          <Box sx={{ mb: 1 }}>
            <Typography variant="overline" sx={{ color: tokens.brand.main }}>Melsoft CyberGuard</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, mt: .5 }}>{challenge ? 'Verify your identity' : 'Welcome back'}</Typography>
            <Typography variant="body2" sx={{ color: tokens.text.secondary, mt: 1 }}>{challenge ? 'Enter the 6-digit code from your authenticator app.' : 'Sign in to your secure operations workspace.'}</Typography>
          </Box>
          {error && (
            <Alert severity="error">
              <strong>{error.code}</strong> — {error.message}
              <br /><small>{error.guidance}</small>
            </Alert>
          )}
          {!challenge ? <>
            <TextField label="Work email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" autoFocus />
            <TextField label="Password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" InputProps={{ endAdornment: <InputAdornment position="end"><IconButton aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword((value) => !value)} edge="end">{showPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}</IconButton></InputAdornment> }} />
          </> : (
            <TextField label="Authenticator code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputProps={{ inputMode: 'numeric', maxLength: 6 }} required autoFocus />
          )}
          <Button type="submit" variant="contained" size="large" disabled={busy} sx={{ mt: 1 }}>
            {busy ? 'Authenticating…' : challenge ? 'Verify and sign in' : 'Sign in'}
          </Button>
          <Typography variant="caption" sx={{ color: tokens.text.muted, textAlign: 'center', pt: 1 }}>Authorised users only · Access is monitored and audited</Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
